import {
  DoorsAndKeysDetails,
  DoorsAndKeysIncidentType,
} from "../../model/Incident/DoorsAndKeysDetails";
import { IncidentUtils } from "../IncidentUtils";
import { Case } from "../../api/models/Case";
import { ImmobilizationType } from "../../model/ImmobilizationType.enum";
import { AppointmentUtils } from "../AppointmentUtils";
import { Appointment } from "../../model/Appointment";
import { PersonDetail } from "../../model/PersonDetail";
import { PersonalDetailsUtils } from "../PersonalDetailsUtils";
import { HighwayUtils } from "../HighwayUtils";
import { LocationUtils } from "../LocationUtils";

export class DoorAndKeysUtils {
  public static serialize(
    data: DoorsAndKeysDetails,
    immobilizationType: ImmobilizationType,
    appointment: Appointment | undefined,
    personDetail: PersonDetail,
    immobilizedOnHighway: boolean | undefined
  ): Partial<Case> {
    return {
      MainCause: "12",
      Comment: this.getCaseComment(
        data,
        personDetail,
        immobilizedOnHighway,
        immobilizationType
      ),
      Priority: this.getPriority(
        data.isLocked,
        immobilizationType,
        appointment
      ),
      ItemsUsedList: [
        {
          ...IncidentUtils.getItemType(),
          LastVehicleStatus: {
            PrimaryFault: this.getPrimaryFault(data.breakdownType),
            Comment: this.getTCSDescription(data),
          },
        },
      ],
    };
  }

  public static getSummary(data: DoorsAndKeysDetails): string {
    return data.breakdownType ? data.breakdownType : "";
  }

  private static getPrimaryFault(breakdownType: string): string {
    switch (breakdownType) {
      case DoorsAndKeysIncidentType.KEY_REMOTE_PROBLEM:
        return "65097";
      case DoorsAndKeysIncidentType.KEY_REMOTE_LOCKED_IN_CAR:
        return "65096";
      case DoorsAndKeysIncidentType.FUEL_CAP_JAMMED:
        return "65059";
      case DoorsAndKeysIncidentType.LOST_KEY_REMOTE:
        return "65097";
      default:
        // Represents DoorsAndKeysIncidentType.OTHER
        return "65076";
    }
  }

  private static getTCSDescription(data: DoorsAndKeysDetails): string {
    return [
      this.getCodeOfVehicleDescriptionByBreakdown(data.breakdownType),
      IncidentUtils.formatAdditionalInformation(data.additionalInformation),
    ]
      .filter(Boolean)
      .join("; ");
  }

  private static getCodeOfVehicleDescriptionByBreakdown(
    breakdownType: string
  ): string | undefined {
    switch (breakdownType) {
      case DoorsAndKeysIncidentType.KEY_REMOTE_PROBLEM:
        return "Callback Fragebaum; Problem mit Schlüssel oder Fernbedienung";
      case DoorsAndKeysIncidentType.KEY_REMOTE_LOCKED_IN_CAR:
        return "Schlüssel eingeschlossen";
      case DoorsAndKeysIncidentType.FUEL_CAP_JAMMED:
        return "Callback THD nach Vervollständigung Dossier durch FA";
      case DoorsAndKeysIncidentType.LOST_KEY_REMOTE:
        return "Schlüssel verloren";
      default:
        // Represents DoorsAndKeysIncidentType.OTHER
        return "Anderes Tür- und Schlüsselproblem";
    }
  }

  private static getCaseComment(
    data: DoorsAndKeysDetails,
    personDetail: PersonDetail,
    immobilizedOnHighway: boolean | undefined,
    immobilizationType: ImmobilizationType
  ) {
    let comment: string[] = [];
    if (data.isLocked) {
      comment.push("Person oder Tier im Auto");
    } else {
      switch (data.breakdownType) {
        case DoorsAndKeysIncidentType.FUEL_CAP_JAMMED:
          comment.push("Callback THD nach Vervollständigung Dossier durch FA");
          break;
        case DoorsAndKeysIncidentType.LOST_KEY_REMOTE:
          comment.push("Callback Pannenart durch THD");
          break;
        default:
          break;
      }
    }

    return [
      LocationUtils.getCaseComment(immobilizedOnHighway, immobilizationType),
      ...comment,
      PersonalDetailsUtils.serializeCaseComment(personDetail),
      ...PersonalDetailsUtils.serializeCoverageComment(personDetail),
    ]
      .filter(Boolean)
      .join("; ");
  }

  private static getPriority(
    isLocked: boolean,
    type: ImmobilizationType,
    appointment: Appointment | undefined
  ): number {
    return isLocked
      ? 1
      : HighwayUtils.serializePriority(
          type,
          AppointmentUtils.getPriority(appointment)
        );
  }
}
